import type { ButtonHTMLAttributes } from 'react';

type Variant = 'primary' | 'ghost' | 'gold';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  variant?: Variant;
  loading?: boolean;
};

const base: React.CSSProperties = {
  minHeight: 48,
  padding: '0 24px',
  borderRadius: 999,
  border: '1px solid transparent',
  fontWeight: 500,
  fontSize: 16,
  cursor: 'pointer',
  transition: 'opacity 0.15s, transform 0.15s, box-shadow 0.15s',
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const variants: Record<Variant, React.CSSProperties> = {
  primary: { background: 'var(--green)', color: 'var(--ink-on-green)' },
  ghost: { background: 'transparent', color: 'var(--green)', borderColor: 'var(--border)' },
  gold: { background: 'var(--gold)', color: 'var(--ink-on-green)' },
};

export function Button({ label, variant = 'primary', loading, disabled, style, type, ...rest }: Props) {
  const isDisabled = disabled || loading;
  // Default to type="button" so a Button inside a <form> doesn't implicitly
  // submit the form when clicked. Every callsite that DOES want submit
  // behaviour passes type="submit" explicitly. Pre-2026-05-18 the default
  // was the browser's implicit "submit" inside forms, which caused the kid
  // photo upload bug: clicking the Upload button submitted the parent kid
  // form (PATCH then navigate) before the file picker callback could run.
  return (
    <button
      {...rest}
      type={type ?? 'button'}
      disabled={isDisabled}
      style={{
        ...base,
        ...variants[variant],
        opacity: isDisabled ? 0.6 : 1,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {loading ? '…' : label}
    </button>
  );
}
